import React from 'react';
import { useAuthStore } from '../../store/authStore';
import { AlertCircle, ArrowRight } from 'lucide-react';
import iconPng from '/icon.png';

const AuthForm: React.FC = () => {
  const { startOIDCLogin, isLoading, error } = useAuthStore();
  const [showOfflineWarning, setShowOfflineWarning] = React.useState(!navigator.onLine);

  React.useEffect(() => {
    const handleOnline = () => setShowOfflineWarning(false);
    const handleOffline = () => setShowOfflineWarning(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleLogin = async () => {
    if (!navigator.onLine) {
      setShowOfflineWarning(true);
      return;
    }
    await startOIDCLogin();
  };

  const handleRegister = () => {
    const url = 'https://accounts.epivalent.com/register';
    if (window.api?.openExternalLink) {
      window.api.openExternalLink(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <style>{`
        @keyframes auth-orb-drift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33%       { transform: translate(40px, -30px) scale(1.08); }
          66%       { transform: translate(-25px, 20px) scale(0.95); }
        }
        @keyframes auth-orb-drift-2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          40%       { transform: translate(-50px, 35px) scale(1.05); }
          70%       { transform: translate(30px, -20px) scale(0.97); }
        }
        @keyframes auth-glow-pulse {
          0%, 100% { opacity: 0.55; }
          50%       { opacity: 0.9; }
        }
        @keyframes auth-card-in {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes auth-hero-in {
          from { opacity: 0; transform: translateX(-20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes auth-grid-scroll {
          from { background-position: 0 0; }
          to   { background-position: 40px 40px; }
        }
        .auth-orb-1 {
          animation: auth-orb-drift 18s ease-in-out infinite, auth-glow-pulse 6s ease-in-out infinite;
        }
        .auth-card-animate {
          animation: auth-card-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .auth-hero-animate {
          animation: auth-hero-in 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .auth-grid-bg {
          background-image:
            linear-gradient(rgba(249,115,22,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(249,115,22,0.05) 1px, transparent 1px);
          background-size: 40px 40px;
          animation: auth-grid-scroll 12s linear infinite;
        }
        .auth-btn-glow:hover:not(:disabled) {
          box-shadow: 0 0 32px rgba(249,115,22,0.5), 0 4px 16px rgba(0,0,0,0.4);
        }

      `}</style>

      <div className="min-h-screen flex bg-bg-primary overflow-hidden">

        {/* ═══════════════ LEFT — HERO BRANDING ═══════════════ */}
        <div className="hidden lg:flex flex-col justify-center relative flex-1 overflow-hidden px-16 xl:px-24">

          {/* Animated background grid */}
          <div className="absolute inset-0 auth-grid-bg pointer-events-none" />

          {/* Gradient overlay from left */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse 80% 70% at 20% 50%, rgba(249,115,22,0.08) 0%, transparent 70%)',
            }}
          />

          {/* Floating orbs */}
          <div
            className="auth-orb-1 absolute rounded-full pointer-events-none"
            style={{
              width: 480,
              height: 480,
              top: '-10%',
              left: '-8%',
              background: 'radial-gradient(circle, rgba(249,115,22,0.22) 0%, rgba(249,115,22,0.05) 50%, transparent 70%)',
              filter: 'blur(1px)',
            }}
          />
          {/* Divider line */}
          <div
            className="absolute right-0 top-0 bottom-0 w-px"
            style={{ background: 'linear-gradient(to bottom, transparent, rgba(249,115,22,0.25) 30%, rgba(249,115,22,0.25) 70%, transparent)' }}
          />

          {/* Hero content */}
          <div className="relative z-10 auth-hero-animate max-w-lg">
            {/* Logo */}
            <div className="flex items-center gap-4 mb-10">
              <div
                className="w-14 h-14 rounded-2xl overflow-hidden flex-shrink-0"
                style={{ boxShadow: '0 0 28px rgba(249,115,22,0.45), 0 4px 16px rgba(0,0,0,0.5)' }}
              >
                <img src={iconPng} alt="Erozion" className="w-full h-full object-cover" />
              </div>
              <span
                className="text-2xl font-black tracking-tight"
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, rgba(249,115,22,0.95) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Erozion
              </span>
            </div>

            {/* Headline */}
            <h1
              className="text-5xl xl:text-6xl font-black leading-[1.05] tracking-tight mb-4"
              style={{
                background: 'linear-gradient(150deg, #ffffff 0%, #f5f0eb 40%, rgba(249,115,22,0.9) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Where it<br />all begins.
            </h1>

            {/* Sub */}
            <p className="text-text-secondary text-lg leading-relaxed mb-12" style={{ maxWidth: '420px' }}>
              Built by gamers, for gamers.<br />
              Launch, host, mod, discover and connect in one place.
            </p>


          </div>
        </div>

        {/* ═══════════════ RIGHT — LOGIN PANEL ═══════════════ */}
        <div className="flex flex-col items-center justify-center w-full lg:w-[420px] xl:w-[460px] flex-shrink-0 relative px-8 py-12">

          {/* Subtle background */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(160deg, rgba(249,115,22,0.04) 0%, transparent 60%)' }}
          />

          <div
            className="auth-card-animate relative w-full max-w-sm"
            style={{ animationDelay: '0.1s' }}
          >
            {/* Mobile logo (only visible on small screens) */}
            <div className="flex flex-col items-center mb-8 lg:hidden">
              <div
                className="w-16 h-16 rounded-2xl overflow-hidden mb-4"
                style={{ boxShadow: '0 0 28px rgba(249,115,22,0.4)' }}
              >
                <img src={iconPng} alt="Erozion" className="w-full h-full object-cover" />
              </div>
              <h1
                className="text-3xl font-black tracking-tight"
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, rgba(249,115,22,0.9) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Erozion
              </h1>
              <p className="text-text-muted text-sm mt-1">Where it all begins.</p>
            </div>

            {/* Card */}
            <div
              className="rounded-2xl p-8"
              style={{
                background: 'rgba(26,26,26,0.85)',
                border: '1px solid rgba(255,255,255,0.07)',
                backdropFilter: 'blur(24px)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 1px 0 rgba(255,255,255,0.05) inset',
              }}
            >
              <div className="mb-7">
                <h2 className="text-2xl font-bold text-text-primary tracking-tight">Bon retour 👋</h2>
                <p className="text-text-muted text-sm mt-1.5">
                  Connecte-toi avec ton compte Epivalent pour continuer.
                </p>
              </div>

              {/* Offline warning */}
              {showOfflineWarning && (
                <div
                  className="flex items-start gap-3 p-3 rounded-xl mb-5 text-sm"
                  style={{ background: 'rgba(250,204,21,0.1)', border: '1px solid rgba(250,204,21,0.3)' }}
                >
                  <AlertCircle className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" />
                  <span className="text-warning">Pas de connexion Internet. Vérifie ton réseau.</span>
                </div>
              )}

              {/* Error */}
              {error && (
                <div
                  className="flex items-start gap-3 p-3 rounded-xl mb-5 text-sm"
                  style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)' }}
                >
                  <AlertCircle className="h-4 w-4 text-error flex-shrink-0 mt-0.5" />
                  <span className="text-error">
                    {error === 'Network Error'
                      ? 'Impossible de contacter le serveur. Réessaie plus tard.'
                      : error}
                  </span>
                </div>
              )}

              {/* Login button */}
              <button
                onClick={handleLogin}
                disabled={isLoading || showOfflineWarning}
                className="auth-btn-glow w-full py-3 px-5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2.5 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: isLoading || showOfflineWarning
                    ? 'rgba(249,115,22,0.5)'
                    : 'linear-gradient(135deg, rgb(249,115,22) 0%, rgb(234,88,12) 100%)',
                  color: '#fff',
                }}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Connexion en cours…
                  </>
                ) : (
                  <>
                    Se connecter avec Epivalent
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
                <span className="text-xs text-text-muted">ou</span>
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
              </div>

              {/* Register */}
              <p className="text-center text-sm text-text-secondary">
                Pas encore de compte Epivalent ?{' '}
                <button
                  onClick={handleRegister}
                  className="font-semibold transition-colors duration-150 underline underline-offset-2"
                  style={{ color: 'rgb(249,115,22)', textDecorationColor: 'rgba(249,115,22,0.4)' }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'rgb(251,146,60)')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'rgb(249,115,22)')}
                >
                  S'inscrire
                </button>
              </p>
            </div>

            {/* Footer note */}
            <p className="text-center text-xs text-text-muted mt-6 leading-relaxed px-2">
              En te connectant, tu acceptes nos{' '}
              <button
                onClick={() => {
                  const url = 'https://epivalent.com/terms';
                  if (window.api?.openExternalLink) window.api.openExternalLink(url);
                  else window.open(url, '_blank', 'noopener,noreferrer');
                }}
                className="underline hover:text-text-secondary transition-colors"
              >
                Conditions d'utilisation
              </button>{' '}
              et notre{' '}
              <button
                onClick={() => {
                  const url = 'https://epivalent.com/privacy';
                  if (window.api?.openExternalLink) window.api.openExternalLink(url);
                  else window.open(url, '_blank', 'noopener,noreferrer');
                }}
                className="underline hover:text-text-secondary transition-colors"
              >
                Politique de confidentialité
              </button>
              .
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthForm;